import { ChiroUpDayJsCommon } from '@chiroup/core/constants/stringConstants';
import { surveyIds } from '@chiroup/core/constants/SurveyIds';
import { decorateChainTitle } from '@chiroup/core/functions/chainTitle';
import { stripHtmlTags } from '@chiroup/core/functions/stripHtmlTags';
import { AppointmentStatuses } from '@chiroup/core/types/Appointment.type';
import { SurveyBehavesAs } from '@chiroup/core/types/Survey.type';
import {
  GraduationCap as AcademicCapIcon,
  CheckCircle as CheckCircleIcon,
  ClipboardCheck as ClipboardDocumentCheckIcon,
  ClipboardList as ClipboardDocumentListIcon,
  File as DocumentIcon,
  Heart as HeartIcon,
  Phone as PhoneIcon,
  Play as PlayIcon,
} from 'lucide-react';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import useAppointments from '../../hooks/useAppointments';
import useAppointmentSurveys from '../../hooks/useAppointmentSurveys';
import FallbackImage from '../common/FallbackImage';
import { Skeleton } from '../common/Skeleton';
import PainLevelSurvey from './PainLevelSurvey';

const Home: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { selectedPatient, language } = usePatientPortal();
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';
  const clinicPath = (selectedPatient?.clinic as { path?: string } | undefined)?.path;
  // Essentials clinics (`ehr: false`) don't have appointment booking
  // or balances surfaced to patients. Hide the next-appointment chip,
  // Book/Call buttons, and the appointments tile entirely for them.
  const hasEhr = !!selectedPatient?.clinic?.ehr;

  // Pull the user's appointments so we can surface the next upcoming one
  // on Home. Scoped to the selected patient so the "Next appointment"
  // surface doesn't pull in a family member's visit by mistake. Shares
  // the React Query cache with the Appointments page (same key) so
  // navigating between them reuses the fetch. Skipped entirely for
  // essentials clinics — there are no appointments to show.
  const { data: appointments } = useAppointments(
    hasEhr ? clinicPath : undefined,
    hasEhr ? selectedPatient?.ID : undefined
  );
  const isLoadingAppointments = hasEhr && appointments === undefined;

  const hour = new Date().getHours();
  const timeGreeting =
    language === 'es'
      ? hour < 12
        ? 'Buenos días'
        : hour < 18
          ? 'Buenas tardes'
          : 'Buenas noches'
      : hour < 12
        ? 'Good morning'
        : hour < 18
          ? 'Good afternoon'
          : 'Good evening';

  const exercises = selectedPatient?.exercises?.filter((e: any) => e.phase !== 2) || [];
  const conditions = selectedPatient?.conditions || [];

  // Pain-level CTA — surfaced whenever the patient has active
  // exercises they could plausibly have done today. Backend
  // overwrites today's entry on resubmit, so we leave the CTA
  // visible all day in case the patient wants to fix a mis-tap.
  const showPainCta = exercises.length > 0;
  const [painSurveyOpen, setPainSurveyOpen] = useState(false);

  const callHref = selectedPatient?.clinic?.phone
    ? `tel:${selectedPatient.clinic.phone}`
    : `${basePath}/clinic`;
  const isCallExternal = !!selectedPatient?.clinic?.phone;

  // Pick the first upcoming Scheduled appointment. The list from
  // `useAppointments` is pre-sorted by startTime ascending, so we can
  // just take the first one that hasn't already happened and isn't
  // canceled. Memoized so we don't recompute on every render.
  const nextAppointment = useMemo(() => {
    if (!appointments?.length) return null;
    const nowMs = Date.now();
    const upcoming = appointments.find((a: any) => {
      if (a.status !== AppointmentStatuses.Scheduled) return false;
      const startMs = dayjs(a.startTime as any).valueOf();

      return Number.isFinite(startMs) && startMs >= nowMs;
    });

    if (!upcoming) return null;

    return {
      id: upcoming.id as string,
      startsAt: dayjs(upcoming.startTime as any).toISOString(),
      // Each appointment carries its own location timezone (joined as
      // `tz` server-side). Fall back to the platform default if truly
      // absent — never to the browser's tz, which would silently drift
      // for patients whose browser tz differs from the clinic's.
      tz: (upcoming.tz as string) || ChiroUpDayJsCommon.defaultTimezone,
      providerName: upcoming.displayValues?.clinicianName || '',
      treatmentName: upcoming.displayValues?.treatmentName || '',
      patientName: upcoming.displayValues?.patientName,
    };
  }, [appointments]);

  const { items: nextAppointmentTasks, isFetching: isLoadingTasks } = useAppointmentSurveys(
    clinicPath,
    nextAppointment?.id
  );
  const outstandingTasksCount = nextAppointmentTasks.filter(t => !t.completedAt).length;
  const tasksVerbiage = {
    title: language === 'es' ? 'Antes de tu visita' : 'Before your visit',
    forNext: language === 'es' ? 'Para tu próxima cita' : 'For your next appointment',
    complete: language === 'es' ? 'Completado' : 'Completed',
    start: language === 'es' ? 'Empezar' : 'Start',
  };

  // ACCS chains produce multiple instances of the same survey for one
  // appointment; label the 2nd+ "(2nd complaint)" so the patient can
  // tell them apart. Restricted to ACCS — built-in (surveyId match) or
  // custom (behavesAs === abbreviatedCCS) — never CCS, Oswestry, etc.
  const isAccsTask = (
    task: (typeof nextAppointmentTasks)[number]
  ): task is Extract<(typeof nextAppointmentTasks)[number], { kind: 'survey' }> => {
    if (task.kind !== 'survey') return false;

    return (
      task.surveyId === surveyIds.abbreviatedCCS ||
      task.behavesAs === SurveyBehavesAs.AbbreviatedCCS
    );
  };

  const accsPositionByTaskId = new Map<string, number>();
  const accsTotalBySurveyId = new Map<string, number>();
  {
    const ordered = [...nextAppointmentTasks].sort((a, b) => {
      const ac = 'createdAt' in a ? a.createdAt : 0;
      const bc = 'createdAt' in b ? b.createdAt : 0;
      return ac - bc;
    });
    const seenBySurveyId = new Map<string, number>();

    for (const t of ordered) {
      if (!isAccsTask(t)) continue;
      const position = (seenBySurveyId.get(t.surveyId) ?? 0) + 1;
      seenBySurveyId.set(t.surveyId, position);
      accsPositionByTaskId.set(t.id, position);
    }

    for (const [sid, total] of seenBySurveyId) {
      accsTotalBySurveyId.set(sid, total);
    }
  }

  const taskTitle = (task: (typeof nextAppointmentTasks)[number]): string => {
    if (task.kind === 'survey') {
      if (isAccsTask(task)) {
        const total = accsTotalBySurveyId.get(task.surveyId) ?? 0;
        const position = accsPositionByTaskId.get(task.id);
        if (total > 1 && position && position > 1) {
          return decorateChainTitle(task.title, position, language);
        }
      }
      return task.title;
    }
    if (task.kind === 'consent') return task.title;
    if (task.kind === 'photoId') return language === 'es' ? 'Identificación con foto' : 'Photo ID';

    if (task.kind === 'insuranceCard') {
      return language === 'es' ? 'Tarjeta de seguro' : 'Insurance card';
    }

    if (task.kind === 'insurance') return language === 'es' ? 'Seguro médico' : 'Insurance';

    return language === 'es' ? 'Información del paciente' : 'Patient information';
  };

  const taskHref = (task: (typeof nextAppointmentTasks)[number]): string | null => {
    if (task.kind === 'survey') return `${basePath}/surveys/${task.id}`;
    if (task.kind === 'consent') return `${basePath}/consents/${task.id}`;
    if (task.kind === 'photoId') return `${basePath}/uploads/photoId/${task.requestId}`;

    if (task.kind === 'insuranceCard') {
      return `${basePath}/uploads/insuranceCard/${task.requestId}`;
    }

    if (task.kind === 'basicInformation') {
      return `${basePath}/forms/basicInformation/${task.requestId}`;
    }

    if (task.kind === 'insurance') return `${basePath}/forms/insurance/${task.requestId}`;

    return null;
  };

  // Render in the appointment's location tz so the time shown here
  // matches every other surface (Appointments page, schedule picker)
  // even when the patient's browser is in a different zone.
  const apptDayjs = nextAppointment ? dayjs(nextAppointment.startsAt).tz(nextAppointment.tz) : null;
  const apptRelativeDay = apptDayjs
    ? (() => {
        const diff = apptDayjs
          .startOf('day')
          .diff(dayjs().tz(nextAppointment!.tz).startOf('day'), 'day');

        if (diff === 0) return language === 'es' ? 'Hoy' : 'Today';
        if (diff === 1) return language === 'es' ? 'Mañana' : 'Tomorrow';

        return apptDayjs.format('ddd, MMM D');
      })()
    : null;
  const apptTime = apptDayjs ? apptDayjs.format('h:mm A') : null;

  const seeAllLabel = language === 'es' ? 'Ver todo' : 'See all';
  const bookLabel = language === 'es' ? 'Reservar' : 'Book';
  const callLabel = language === 'es' ? 'Llamar al consultorio' : 'Call Clinic';

  const adviceCount = selectedPatient?.advice?.length || 0;
  const filesCount = selectedPatient?.fileAttachments?.length || 0;
  const exercisesPreview = exercises.slice(0, 4);

  return (
    <div className="flex flex-col gap-6">
      {/*
       * Hero — hybrid mobile/desktop layout.
       *  - mobile: a single tinted card. Greeting on top, next-appointment
       *    chip beneath a divider, Book + Call action row at the bottom.
       *  - md+: split panel. The left zone (3/5) keeps the tinted
       *    background and stacks greeting + Book CTA. The right zone
       *    (2/5) is a white surface that features the next appointment
       *    with a slightly larger date chip — turning the card into two
       *    clearly separated visual zones instead of one wide row.
       */}
      <div
        className={`rounded-3xl overflow-hidden ${hasEhr ? 'md:grid md:grid-cols-5' : ''} dark:ring-1 dark:ring-white/10`}
        style={{ backgroundColor: `${primaryColor}12` }}
      >
        {/* Left / main zone — full width on mobile, 3/5 on desktop
            when EHR (next-appointment panel takes the other 2/5).
            Essentials clinics drop the right zone entirely so the
            greeting + Call button take the full card width. */}
        <div className={`p-5 sm:p-6 md:p-7 ${hasEhr ? 'md:col-span-3' : ''} flex flex-col gap-5`}>
          {selectedPatient && (
            <div>
              <p
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: primaryColor }}
              >
                {timeGreeting}
              </p>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 mt-1">
                {selectedPatient.fname} {selectedPatient.lname}
              </h1>
            </div>
          )}

          {/* Mobile-only next-appointment chip. On desktop the right
              zone surfaces this with its own bigger treatment.
              Renders a skeleton while `useAppointments` is in flight
              so the hero card doesn't grow / shrink as data lands.
              Hidden entirely for essentials clinics. */}
          {!hasEhr ? null : isLoadingAppointments ? (
            <div className="md:hidden flex items-center gap-3 pt-4 border-t border-gray-950/5 dark:border-white/10">
              <Skeleton className="size-14 rounded-2xl shrink-0" />
              <div className="flex-1 flex flex-col gap-1.5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-4 w-3/5" />
                <Skeleton className="h-3 w-2/5" />
              </div>
            </div>
          ) : (
            nextAppointment &&
            apptDayjs && (
              <Link
                to={`${basePath}/appointments`}
                className="md:hidden flex items-center gap-3 pt-4 border-t border-gray-950/5 dark:border-white/10 active:opacity-70"
              >
                <div
                  className="shrink-0 flex flex-col items-center justify-center rounded-2xl px-3 py-2 min-w-14 text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-white/80">
                    {apptDayjs.format('MMM')}
                  </span>
                  <span className="text-xl font-bold tabular-nums leading-6">
                    {apptDayjs.format('D')}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {language === 'es' ? 'Próxima cita' : 'Next appointment'}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate mt-0.5">
                    {apptRelativeDay} · {apptTime}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {nextAppointment.treatmentName} · {nextAppointment.providerName}
                  </p>
                </div>
                <Icon
                  icon={Icons.chevronRight}
                  className="shrink-0 h-4 w-4 text-gray-400 dark:text-gray-500"
                />
              </Link>
            )
          )}

          <div className="flex items-center gap-2 md:mt-auto">
            {/* Book CTA — patient-side booking is an EHR-only feature.
                Essentials clinics get the Call button only. */}
            {hasEhr && (
              <Link
                to={`${basePath}/schedule`}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full py-3 sm:px-10 text-sm font-semibold text-white active:scale-[0.98] transition-transform"
                style={{ backgroundColor: primaryColor }}
              >
                <Icon icon={Icons.calendar} className="h-4 w-4" />
                {bookLabel}
              </Link>
            )}
            {/* Call only makes sense on phones — `tel:` links dial natively
                on mobile but are dead weight on desktop. Hidden at sm+
                regardless of EHR/essentials. For essentials, that
                means the action row is empty on desktop, which is
                fine — the hero just shows the greeting. */}
            {isCallExternal ? (
              <a
                href={callHref}
                className="flex-1 sm:hidden inline-flex items-center justify-center gap-1.5 rounded-full py-3 px-5 text-sm font-semibold bg-white dark:bg-white/5 text-gray-900 dark:text-gray-100 border border-gray-950/10 dark:border-white/15 active:scale-[0.98] transition-transform"
              >
                <PhoneIcon className="h-4 w-4" />
                {callLabel}
              </a>
            ) : (
              <Link
                to={`${basePath}/clinic`}
                className="flex-1 sm:hidden inline-flex items-center justify-center gap-1.5 rounded-full py-3 px-5 text-sm font-semibold bg-white dark:bg-white/5 text-gray-900 dark:text-gray-100 border border-gray-950/10 dark:border-white/15 active:scale-[0.98] transition-transform"
              >
                <PhoneIcon className="h-4 w-4" />
                {callLabel}
              </Link>
            )}
          </div>
        </div>

        {/* Right zone — desktop only. White surface that features the
            next appointment with a larger date chip. Skeleton state
            keeps the panel populated while `useAppointments` resolves
            so the split-panel hero stays balanced. Hidden entirely
            for essentials clinics (no appointments to surface). */}
        {!hasEhr ? null : isLoadingAppointments ? (
          <div className="hidden md:flex md:col-span-2 p-7 bg-white dark:bg-[var(--color-bg-card)] items-center gap-4">
            <Skeleton className="size-16 rounded-2xl shrink-0" />
            <div className="flex-1 flex flex-col gap-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ) : nextAppointment && apptDayjs ? (
          <Link
            to={`${basePath}/appointments`}
            className="hidden md:flex md:col-span-2 p-7 bg-white dark:bg-[var(--color-bg-card)] items-center gap-4 group"
          >
            <div
              className="shrink-0 flex flex-col items-center justify-center rounded-2xl px-3 py-2 min-w-16 text-white"
              style={{ backgroundColor: primaryColor }}
            >
              <span className="text-[10px] font-semibold uppercase tracking-wider text-white/80">
                {apptDayjs.format('MMM')}
              </span>
              <span className="text-2xl font-bold tabular-nums leading-7">
                {apptDayjs.format('D')}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {language === 'es' ? 'Próxima cita' : 'Next appointment'}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate mt-0.5">
                {apptRelativeDay} · {apptTime}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {nextAppointment.treatmentName} · {nextAppointment.providerName}
              </p>
            </div>
            <Icon
              icon={Icons.chevronRight}
              className="shrink-0 h-4 w-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors"
            />
          </Link>
        ) : (
          <div className="hidden md:flex md:col-span-2 p-7 bg-white dark:bg-[var(--color-bg-card)] items-center justify-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              {language === 'es' ? 'No tienes citas próximas.' : 'No upcoming appointments.'}
            </p>
          </div>
        )}
      </div>

      {/* Daily exercise check-in. Visible only when there are
          active exercises and the patient hasn't logged a pain
          level today — the modal closes the loop and `refreshPatientData`
          flips `completedToday` so this card disappears until
          tomorrow. */}
      {showPainCta && (
        <button
          type="button"
          onClick={() => setPainSurveyOpen(true)}
          className="portal-card flex items-center gap-3 p-4 text-left active:scale-[0.98] transition-transform hover:ring-2 hover:ring-[--ring]"
          style={{ ['--ring' as any]: primaryColor } as React.CSSProperties}
        >
          <span
            className="size-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
            aria-hidden="true"
          >
            <CheckCircleIcon className="size-5" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
              {language === 'es'
                ? '¿Completó sus ejercicios hoy?'
                : 'Did you do your exercises today?'}
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {language === 'es'
                ? 'Marque como completado y registre su nivel de dolor.'
                : 'Mark them complete and log how you’re feeling.'}
            </span>
          </span>
          <Icon
            icon={Icons.chevronRight}
            className="size-4 text-gray-300 dark:text-gray-600 shrink-0"
          />
        </button>
      )}

      {/* Pre-visit tasks for the next appointment. Surveys, consents,
          and the legacy Photo ID / Insurance / Demographics steps all
          surface here. Hidden when the appointment has no tasks (most
          recurring visits). */}
      {nextAppointment && (isLoadingTasks || nextAppointmentTasks.length > 0) && (
        <div className="portal-card p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className="size-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <ClipboardDocumentListIcon className="size-5" style={{ color: primaryColor }} />
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {tasksVerbiage.title}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{tasksVerbiage.forNext}</p>
              </div>
            </div>
            {!isLoadingTasks && outstandingTasksCount > 0 && (
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full text-white shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                {outstandingTasksCount}
              </span>
            )}
          </div>

          {isLoadingTasks ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full rounded-xl" />
              <Skeleton className="h-12 w-full rounded-xl" />
            </div>
          ) : (
            <ul className="flex flex-col divide-y divide-gray-950/5 dark:divide-white/10">
              {nextAppointmentTasks.map((task, idx) => {
                const isComplete = !!task.completedAt;
                const href = taskHref(task);
                const title = taskTitle(task);
                const key =
                  task.kind === 'survey' || task.kind === 'consent'
                    ? `${task.kind}-${task.id}`
                    : `${task.kind}-${task.requestId}-${idx}`;

                const rightLabel = isComplete
                  ? tasksVerbiage.complete
                  : href
                    ? tasksVerbiage.start
                    : task.kind === 'survey'
                      ? language === 'es'
                        ? 'Cuestionario'
                        : 'Survey'
                      : task.kind === 'consent'
                        ? language === 'es'
                          ? 'Consentimiento'
                          : 'Consent'
                        : task.kind === 'photoId'
                          ? language === 'es'
                            ? 'Foto ID'
                            : 'Photo ID'
                          : task.kind === 'insuranceCard'
                            ? language === 'es'
                              ? 'Tarjeta'
                              : 'Card'
                            : task.kind === 'insurance'
                              ? language === 'es'
                                ? 'Seguro'
                                : 'Insurance'
                              : language === 'es'
                                ? 'Información'
                                : 'Info';

                const inner = (
                  <>
                    {isComplete ? (
                      <CheckCircleIcon
                        className="size-5 shrink-0 text-gray-400 dark:text-gray-500"
                        aria-hidden="true"
                      />
                    ) : (
                      <span
                        className="size-5 rounded-full border-2 shrink-0"
                        style={{ borderColor: primaryColor }}
                        aria-hidden="true"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate ${
                          isComplete
                            ? 'text-gray-500 dark:text-gray-400 line-through'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {title}
                      </p>
                    </div>
                    <span
                      className={`text-xs font-semibold shrink-0 ${
                        isComplete
                          ? 'text-gray-400 dark:text-gray-500'
                          : href
                            ? 'group-hover:underline'
                            : 'text-gray-500 dark:text-gray-400'
                      }`}
                      style={!isComplete && href ? { color: primaryColor } : undefined}
                    >
                      {rightLabel}
                    </span>
                    {href && (
                      <Icon
                        icon={Icons.chevronRight}
                        className={`shrink-0 h-4 w-4 ${
                          isComplete
                            ? 'text-gray-300 dark:text-gray-600'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}
                      />
                    )}
                  </>
                );

                return (
                  <li key={key} className="py-2 first:pt-0 last:pb-0">
                    {href ? (
                      <Link
                        to={href}
                        className="flex items-center gap-3 group"
                        aria-disabled={isComplete}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <div className="flex items-center gap-3">{inner}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Quick-link tile grid — hidden on phones (BottomNav already
          covers navigation there), 2 cols on tablet, 4 on desktop.
          Each tile shows section name + current count. */}
      <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Link
          to={`${basePath}/exercises`}
          className="portal-card flex flex-col gap-3 p-4 active:scale-[0.98] transition-transform"
        >
          <div
            className="size-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <AcademicCapIcon className="size-5" style={{ color: primaryColor }} />
          </div>
          <div>
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {hcVerbiage.exercises[language]}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {exercises.length} {language === 'es' ? 'asignados' : 'in plan'}
            </p>
          </div>
        </Link>
        <Link
          to={`${basePath}/conditions`}
          className="portal-card flex flex-col gap-3 p-4 active:scale-[0.98] transition-transform"
        >
          <div
            className="size-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <HeartIcon className="size-5" style={{ color: primaryColor }} />
          </div>
          <div>
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {hcVerbiage.conditions[language]}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {conditions.length} {language === 'es' ? 'rastreadas' : 'tracked'}
            </p>
          </div>
        </Link>
        <Link
          to={`${basePath}/advice`}
          className="portal-card flex flex-col gap-3 p-4 active:scale-[0.98] transition-transform"
        >
          <div
            className="size-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <ClipboardDocumentCheckIcon className="size-5" style={{ color: primaryColor }} />
          </div>
          <div>
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {hcVerbiage.advice[language]}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {adviceCount} {language === 'es' ? 'artículos' : 'items'}
            </p>
          </div>
        </Link>
        <Link
          to={`${basePath}/health`}
          className="portal-card flex flex-col gap-3 p-4 active:scale-[0.98] transition-transform"
        >
          <div
            className="size-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <DocumentIcon className="size-5" style={{ color: primaryColor }} />
          </div>
          <div>
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {hcVerbiage.files[language]}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filesCount} {language === 'es' ? 'documentos' : 'documents'}
            </p>
          </div>
        </Link>
      </div>

      {/* Today's Exercises preview — 2-col list at md+ so the row of
          tiles above doesn't sit alone on a wide screen. */}
      {exercisesPreview.length > 0 && (
        <section>
          <header className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {hcVerbiage.exercises[language]}
            </h2>
            <Link
              to={`${basePath}/exercises`}
              className="text-sm font-medium"
              style={{ color: primaryColor }}
            >
              {seeAllLabel}
            </Link>
          </header>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {exercisesPreview.map((exercise: any) => (
              <Link
                key={exercise.ID}
                to={`${basePath}/exercises/${exercise.ID}`}
                className="portal-card flex items-center gap-3 p-3.5 active:scale-[0.98] transition-transform"
              >
                <FallbackImage
                  src={
                    exercise.img
                      ? `${exercise.img}${exercise.img.includes('?') ? '&' : '?'}w=96&q=80`
                      : null
                  }
                  alt=""
                  imgClassName="shrink-0 size-12 rounded-xl object-cover"
                  fallback={
                    <div
                      className="shrink-0 size-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}15` }}
                    >
                      <PlayIcon className="size-5" style={{ color: primaryColor }} />
                    </div>
                  }
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {exercise.name}
                  </p>
                  {exercise.descr && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                      {stripHtmlTags(exercise.descr)}
                    </p>
                  )}
                </div>
                <Icon
                  icon={Icons.chevronRight}
                  className="size-4 text-gray-300 dark:text-gray-600 shrink-0"
                />
              </Link>
            ))}
          </div>
        </section>
      )}

      <PainLevelSurvey
        open={painSurveyOpen}
        setOpen={setPainSurveyOpen}
        language={language === 'es' ? 'es' : 'en'}
        primaryColor={primaryColor}
      />
    </div>
  );
};

export default Home;
